import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, rgbToHex } from '@mui/material';
import { createClassFromLiteSpec } from 'react-vega-lite';
import { getFormattedStats } from '../lib/charts';
import ApexChart from './Chart';
import { AdjustmentGroups } from './AdjustmentGroups';
import { usePermission } from '../hooks/use-permission';
import { calculateStats } from '../lib/stats';

AdjustmentTab.propTypes = {
  isVisible: PropTypes.bool,
  xLabel: PropTypes.string,
  colors: PropTypes.object,
  floor: PropTypes.number,
  ceiling: PropTypes.number.isRequired,
  multipleOf: PropTypes.number,
  fieldAdjustment: PropTypes.number,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      adjustment: PropTypes.number.isRequired,
      acres: PropTypes.number.isRequired,
    }),
  ).isRequired,
  onFieldAdjustment: PropTypes.func.isRequired,
  onGroupsAdjustment: PropTypes.func.isRequired,
  revertRef: PropTypes.object,
  stats: PropTypes.object,
};

export function AdjustmentTab(props) {
  const {
    floor = 0,
    ceiling,
    colors,
    fieldAdjustment,
    groups,
    isVisible,
    multipleOf,
    onFieldAdjustment,
    onGroupsAdjustment,
    revertRef,
    xLabel = 'Rate (lbs/ac)',
  } = props;

  const canAdjust = usePermission('RUN_RX');
  const [selectedFeature, setSelectedFeature] = useState(-1);
  const [stats, setStats] = useState(props.stats);
  const [mapSpec, setMapSpec] = useState(null);
  const [mapData, setMapData] = useState({
    values: { type: 'FeatureCollection', features: [] },
    format: { type: 'json', property: 'features' },
  });

  const formattedStats = useMemo(
    () => stats && colors && getFormattedStats(stats, colors),
    [colors, stats],
  );

  useEffect(() => {
    if (!formattedStats) return;

    const domain = formattedStats.chart.values.map((cv) => cv.label);
    const range = formattedStats.colors.map((c) => rgbToHex(c));

    setMapSpec(
      buildMapSpec(
        'properties.TGT_RATE',
        'Rate lbs/ac',
        domain,
        range,
        selectedFeature,
      ),
    );

    setMapData({
      values: { type: 'FeatureCollection', features: formattedStats.features },
      format: { type: 'json', property: 'features' },
    });
  }, [formattedStats, selectedFeature]);

  const Map = useMemo(
    () => (mapSpec ? createClassFromLiteSpec(mapSpec) : null),
    [mapSpec],
  );

  if (!isVisible) return <Box />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <ApexChart
        xLabel={xLabel}
        yLabel="Acres"
        stats={formattedStats}
        chartWidth="490px"
        decimals={0}
        mapComponent={
          Map ? <Map style={{ paddingTop: '20px' }} data={mapData} /> : null
        }
      />
      {canAdjust && (
        <AdjustmentGroups
          floor={floor}
          ceiling={ceiling}
          multipleOf={multipleOf}
          fieldAdjustment={fieldAdjustment}
          onFieldAdjustment={adjustFullField}
          groups={groups}
          onGroupAdjustments={adjustGroups}
          revertRef={revertRef}
          onFeatureSelected={setSelectedFeature}
        />
      )}
    </Box>
  );

  function adjustFullField(adjustment) {
    onFieldAdjustment(adjustment);
  }

  function adjustGroups(adjustedGroups) {
    onGroupsAdjustment(adjustedGroups);

    const distribution = adjustedGroups.map((g) => ({
      label: g.adjustedValue,
      value: g.acres,
      rate: g.adjustedValue,
      acres: g.acres,
    }));

    setStats((current) => ({
      ...current,
      distribution,
      stats: calculateStats(distribution),
      features: updateFeatures(current.features, adjustedGroups),
    }));
  }
}

function updateFeatures(features, groups) {
  return features.map((feature) => {
    const group = groups.find((g) => g.value === feature.properties.BASE_RATE);

    return group
      ? {
          ...feature,
          properties: { ...feature.properties, TGT_RATE: group.adjustedValue },
        }
      : feature;
  });
}

function buildMapSpec(attributeName, title, domain, range, selectedFeature) {
  return {
    height: 200,
    width: 200,
    padding: 5,
    mark: { type: 'geoshape' },
    projection: { type: 'mercator' },
    encoding: {
      color: {
        type: 'quantitative',
        field: attributeName,
        title,
        scale: { domain, range, type: 'linear' },
      },
      stroke: {
        condition: {
          test: `datum.properties.BASE_RATE == ${selectedFeature}`,
          value: 'black',
        },
      },
      strokeWidth: {
        condition: {
          test: `datum.properties.BASE_RATE == ${selectedFeature}`,
          value: 1,
        },
      },
    },
  };
}
